/**
 * MIDI Device Panel Component
 * UI for selecting and managing external MIDI devices
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { DropdownPortal } from '../ui/dropdown-portal';
import {
  Usb,
  Piano,
  Plug,
  PlugZap,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  X,
  ChevronDown,
  Activity,
  ExternalLink,
  Download,
  Chrome,
  Globe,
} from 'lucide-react';
import { getBrowserInfo, getSetupInstructions, getMIDIStatusMessage } from '../../utils/midiCompatibility';

export const MIDIDevicePanel = ({
  isOpen,
  onClose,
  isSupported,
  hasAccess,
  isRequesting,
  error,
  inputs,
  selectedInputId,
  isEnabled,
  onRequestAccess,
  onSelectInput,
  onToggleEnabled,
  lastActivity,
  anchorRef,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const deviceBtnRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const panelRef = useRef(null);
  
  // Browser detection
  const browserInfo = useMemo(() => getBrowserInfo(), []);
  const setupInstructions = useMemo(() => getSetupInstructions(browserInfo), [browserInfo]);
  const statusMessage = useMemo(() => getMIDIStatusMessage(browserInfo, isSupported), [browserInfo, isSupported]);
  
  const selectedInput = inputs.find(i => i.id === selectedInputId);
  
  // Calculate position based on anchor element
  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: Math.max(16, rect.left - 280 + rect.width), // Align right edge with button
      });
    }
  }, [isOpen, anchorRef]);
  
  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && 
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);
  
  if (!isOpen) return null;
  
  const panelContent = (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="fixed w-80 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, zIndex: 9999 }}
      data-testid="midi-device-panel"
    >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <Piano className="w-5 h-5 text-purple-400" />
              <span className="font-medium">External MIDI</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-7 w-7 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Browser Status Badge */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              statusMessage.type === 'success' 
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : statusMessage.type === 'warning'
                ? 'bg-amber-500/10 border border-amber-500/30'
                : 'bg-red-500/10 border border-red-500/30'
            }`}>
              {statusMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-400" />
              )}
              <span className={
                statusMessage.type === 'success' ? 'text-emerald-400' :
                statusMessage.type === 'warning' ? 'text-amber-400' : 'text-red-400'
              }>
                {browserInfo.name} - {statusMessage.message}
              </span>
            </div>

            {/* Browser Setup Instructions (Safari/Old Firefox) */}
            {!isSupported && setupInstructions && (
              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Download className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-amber-400">{setupInstructions.title}</h3>
                  </div>
                  <p className="text-sm text-zinc-400 mb-4">{setupInstructions.subtitle}</p>
                  
                  {/* Step by step instructions */}
                  <div className="space-y-2 mb-4">
                    {setupInstructions.steps.map((step, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="text-zinc-300">{step}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Download links */}
                  <div className="flex flex-wrap gap-2">
                    {setupInstructions.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-lg text-amber-400 text-sm transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        {link.label}
                      </a>
                    ))}
                  </div>
                  
                  {/* Alternative suggestion */}
                  <div className="mt-4 pt-3 border-t border-amber-500/20">
                    <p className="text-xs text-zinc-500 flex items-center gap-2">
                      <Chrome className="w-4 h-4" />
                      {setupInstructions.alternative}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Browser Support Status - Only show if truly unsupported and no instructions */}
            {!isSupported && !setupInstructions && (
              <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-400">Not Supported</p>
                  <p className="text-zinc-400 mt-1">
                    Your browser does not support Web MIDI API. Try Chrome, Edge, or Opera.
                  </p>
                </div>
              </div>
            )}
            
            {/* Request Access Button */}
            {isSupported && !hasAccess && (
              <div className="space-y-3">
                <p className="text-sm text-zinc-400">
                  Connect your MIDI keyboard or controller to play and record.
                </p>
                <Button
                  onClick={onRequestAccess}
                  disabled={isRequesting}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="midi-connect-btn"
                >
                  {isRequesting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Requesting Access...
                    </>
                  ) : (
                    <>
                      <Usb className="w-4 h-4 mr-2" />
                      Connect MIDI Devices
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Error Display */}
            {error && (
              <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-400">Connection Error</p>
                  <p className="text-zinc-400 mt-1">{error}</p>
                </div>
              </div>
            )}
            
            {/* Device List */}
            {hasAccess && (
              <>
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">MIDI Input</span>
                  <Button
                    variant={isEnabled ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onToggleEnabled(!isEnabled)}
                    className={isEnabled ? 'bg-green-600 hover:bg-green-700' : 'border-zinc-700'}
                    data-testid="midi-enable-toggle"
                  >
                    {isEnabled ? (
                      <>
                        <PlugZap className="w-4 h-4 mr-1" />
                        Enabled
                      </>
                    ) : (
                      <>
                        <Plug className="w-4 h-4 mr-1" />
                        Disabled
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Device Selector */}
                {inputs.length === 0 ? (
                  <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                    <Usb className="w-5 h-5 text-zinc-500" />
                    <div className="text-sm">
                      <p className="text-zinc-400">No MIDI devices detected</p>
                      <p className="text-zinc-500 text-xs mt-1">
                        Connect a MIDI device and it will appear here
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <Button
                      ref={deviceBtnRef}
                      variant="outline"
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="w-full justify-between border-zinc-700 bg-zinc-800/50"
                      data-testid="midi-device-selector"
                    >
                      <span className="flex items-center gap-2 truncate">
                        {selectedInput ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            <span className="truncate">{selectedInput.name}</span>
                          </>
                        ) : (
                          <>
                            <Usb className="w-4 h-4 text-zinc-500" />
                            <span>Select a device</span>
                          </>
                        )}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {/* Dropdown via portal */}
                    <DropdownPortal
                      triggerRef={deviceBtnRef}
                      isOpen={showDropdown}
                      onClose={() => setShowDropdown(false)}
                      width="trigger"
                    >
                      <div className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
                          {inputs.map((input) => (
                            <button
                              key={input.id}
                              onClick={() => {
                                onSelectInput(input.id);
                                setShowDropdown(false);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-zinc-700 transition-colors ${
                                input.id === selectedInputId ? 'bg-zinc-700' : ''
                              }`}
                              data-testid={`midi-device-option-${input.id}`}
                            >
                              <CheckCircle2 
                                className={`w-4 h-4 ${input.id === selectedInputId ? 'text-green-400' : 'text-transparent'}`} 
                              />
                              <div className="flex-1 truncate">
                                <p className="truncate">{input.name}</p>
                                <p className="text-xs text-zinc-500 truncate">{input.manufacturer}</p>
                              </div>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                input.state === 'connected' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-zinc-600 text-zinc-400'
                              }`}>
                                {input.state}
                              </span>
                            </button>
                          ))}
                      </div>
                    </DropdownPortal>
                  </div>
                )}
                
                {/* Activity Indicator */}
                {selectedInput && isEnabled && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Activity className={`w-4 h-4 ${lastActivity ? 'text-green-400' : 'text-zinc-600'}`} />
                    <span>{lastActivity ? 'Receiving MIDI data' : 'Waiting for input...'}</span>
                  </div>
                )}
                
                {/* Help Text */}
                <div className="text-xs text-zinc-500 space-y-1 pt-2 border-t border-zinc-800">
                  <p>• Play your MIDI device to control the selected instrument track</p>
                  <p>• Start MIDI recording to capture your performance</p>
                  <p>• Velocity is supported for expressive playing</p>
                </div>
              </>
            )}
          </div>
        </motion.div>
  );
  
  // Use a portal to render outside the overflow-hidden container
  return createPortal(
    <AnimatePresence>
      {panelContent}
    </AnimatePresence>,
    document.body
  );
};

export default MIDIDevicePanel;
